import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

// Same autoplay engagement gate as slideshow.js (its own copy — the two
// modules load independently): rotation waits for the visitor's first
// gesture so load-time frames stay visually stable for Speed Index, and the
// bar's visibility transition (a non-composited animation) doesn't fire
// during load. Only isTrusted events count, and 'scroll' listens in the
// bubble phase so element/carousel init scrolls can't unlock the gate —
// see the matching comment in slideshow.js. Ported from SeroVital 9bba932.
let userHasInteracted = false;
const markInteracted = (/** @type {Event} */ event) => {
  if (event.isTrusted) userHasInteracted = true;
};
for (const eventName of ['pointerdown', 'keydown', 'wheel', 'touchstart']) {
  window.addEventListener(eventName, markInteracted, { passive: true, capture: true });
}
window.addEventListener('scroll', markInteracted, { passive: true });

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * NOVEX CUSTOMIZATION (registered in docs/native-file-registry.md, 2026-08-14).
   * True while keyboard focus is inside the bar. WCAG 2.2.2 needs a pause
   * mechanism and vanilla binds mouseenter/mouseleave ONLY, so a keyboard user
   * had none — on every page, at a 5s interval, with a link in slide 1.
   * A SEPARATE flag from `paused` on purpose: `mouseleave` fires `resume()`,
   * which must not clear a focus pause. Public (not #private) so QA can assert
   * it from the console. Same pattern as sections/testimonial-marquee.liquid.
   * @type {boolean}
   */
  focusPaused = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (
        this.matches(':hover') ||
        this.focusPaused ||
        document.hidden ||
        !userHasInteracted ||
        prefersReducedMotion()
      ) {
        return;
      }

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused || this.focusPaused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
      slide.inert = index !== relativeIndex;
    });
  }

  /**
   * Pause the slideshow when the page is hidden. Uses suspend() — not
   * pause() — matching slideshow.js: pause() sets the `paused` attribute,
   * which resume() treats as a hard stop, so a single tab hide/show cycle
   * would kill rotation permanently (pre-existing upstream bug; ported
   * fix from SeroVital 9bba932).
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  /**
   * Stop rotating while keyboard focus is inside (WCAG 2.2.2). `suspend()`, not
   * `pause()` — a focus pause is temporary, exactly like the hover pause, and
   * `pause()` is terminal here (`resume()` early-returns on `paused`).
   */
  #handleFocusIn = () => {
    this.focusPaused = true;
    this.suspend();
  };

  /**
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    // Focus moving between controls inside the bar is not a blur.
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    this.focusPaused = false;
    this.resume();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
